import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:4000',
});

// Request interceptor: attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ry_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ry_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) =>
  api.post('/api/auth/login', { email, password });

export const changePassword = (currentPassword, newPassword) =>
  api.post('/api/auth/change-password', { currentPassword, newPassword });

// Password reset (self-serve, used while logged out — public endpoints)
export const requestPasswordReset = (email) =>
  api.post('/api/auth/forgot-password', { email });

export const resetPassword = (token, newPassword) =>
  api.post('/api/auth/reset-password', { token, newPassword });

// User
export const getMe = () => api.get('/api/user/me');

export const updateUserName = (firstName, lastName) =>
  api.post('/api/user/name', { firstName, lastName });

// 7-day BMS booking-lock check. Returns { canBook, unlockAt }.
// Used by BookingModal to gate the calendar from EVERY entry point at once.
export const getCanBookBms = () => api.get('/api/user/can-book-bms');

// Progress
export const getProgress = () => api.get('/api/progress');

export const updateVideoProgress = (dayNum, percent) =>
  api.post(`/api/progress/day/${dayNum}/video`, { percent });

export const completeDay = (dayNum) =>
  api.post(`/api/progress/day/${dayNum}/complete`);

export const completeOnboarding = () =>
  api.post('/api/progress/onboarding/complete');

// Journal
export const saveJournal = (dayNum, entries) =>
  api.post(`/api/journal/${dayNum}`, { entries });

export const getJournal = (dayNum) =>
  api.get(`/api/journal/${dayNum}`);

export const getAllJournalEntries = () =>
  api.get('/api/journal');

// Check-in
export const saveCheckin = (dayNum, calm, connected, clarity) =>
  api.post('/api/checkin', { dayNum, calm, connected, clarity });

// Meditation listen-tracking — backend marks meditation_complete when percent >= 90.
export const saveMeditationProgress = (dayNum, percent) =>
  api.post(`/api/progress/day/${dayNum}/meditation`, { percent });

// Booking
export const trackBookingClick = () =>
  api.post('/api/user/booking-click');

export const getBooking = () =>
  api.get('/api/user/booking');

// Reschedule an existing BMS booking to a new time. JWT-gated; the backend
// re-validates the slot + floor and proxies to booking-hub. Returns the axios
// response so callers can read status (200 / 409 / 502 / 403) + data.
export const rescheduleBooking = ({ appointmentId, start, end }) =>
  api.post('/api/user/reschedule', { appointmentId, start, end });

// ─── Booking Hub proxies (JWT-gated) ──────────────────────────────────────
// These three used to be direct browser calls to bookings.benmilston.com
// carrying VITE_RY_PORTAL_API_KEY. Vite inlines VITE_-prefixed variables into
// the built bundle, so that key was readable by anyone viewing source, and it
// was the ONLY thing guarding an endpoint that accepts an arbitrary email.
// They now go through the RY backend, which holds the key server-side and —
// for the booking itself — supplies the email from the caller's own ry_users
// row rather than trusting anything sent from the browser.
//
// The backend passes Booking Hub's status and JSON body through unchanged, so
// callers still branch on 409 (slot taken) and on data.reason
// (bms_lock_active / bms_window_exceeded) exactly as before.

export const getBmsSlots = () => api.get('/api/user/bms-slots');

export const getRescheduleSlots = (appointmentId) =>
  api.get('/api/user/reschedule-slots', { params: { appointmentId } });

// No email parameter by design — the backend derives it from the JWT.
export const bookBms = ({ start_time, end_time, first_name, last_name, client_timezone }) =>
  api.post('/api/user/book-bms', { start_time, end_time, first_name, last_name, client_timezone });

// Support
export const sendSupportMessage = (message) =>
  api.post('/api/support', { message });

// Testimonial
export const submitTestimonial = (data) => api.post('/api/testimonial', data);

// BMS Pre-call Form (one-time submit)
export const getBmsForm = () => api.get('/api/bms-form');
export const submitBmsForm = (data) => api.post('/api/bms-form', data);

// Call Day Settings (client-facing)
export const getCallDaySettings = () => api.get('/api/user/call-day-settings');

// Admin
export const getAdminStats = () => api.get('/api/admin/stats');
export const getAdminFunnel = () => api.get('/api/admin/funnel');
export const getAdminClients = () => api.get('/api/admin/clients');
export const getAdminClientDetail = (userId) => api.get(`/api/admin/clients/${userId}`);
export const getAdminTestimonials = () => api.get('/api/testimonial/all');
// from/to are OMITTED unless given, so the all-time call is byte-identical to
// what it always sent. The window filters by SEND date — see the endpoint.
export const getEmailStats = (from, to) =>
  api.get('/api/admin/email-stats', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } });
export const getClientEmailHistory = (userId) => api.get(`/api/admin/clients/${userId}/email-history`);
// Users → Video tab: course video sittings + pre-purchase masterclass/sales watching (read-only).
export const getAdminClientVideoWatch = (userId) => api.get(`/api/admin/clients/${userId}/video-watch`);

// Admin Dashboard (new)
export const getAdminStatsFull = () => api.get('/api/admin/stats-full');
export const getAdminAlerts = () => api.get('/api/admin/alerts');
export const getAdminFunnelFull = () => api.get('/api/admin/funnel-full');
export const getAdminCourseProgress = () => api.get('/api/admin/course-progress');
export const getAdminRevenueSummary      = () => api.get('/api/admin/revenue-summary');
export const getAdminRevenueDetail       = () => api.get('/api/admin/revenue-detail');
export const getAdminRevenueSplit        = () => api.get('/api/admin/revenue-split');
export const getAdminAvgDaysToPurchase   = () => api.get('/api/admin/avg-days-to-purchase');
export const getAdminLeadsStats = (from, to, paidOnly, extra = {}) =>
  // paidOnly is OMITTED unless true — the endpoints are inert without it,
  // so Pages & Masterclass keeps sending exactly the request it always has.
  api.get('/api/admin/leads-stats', { params: { from, to, ...(paidOnly ? { paidOnly: 1 } : {}), ...extra } });

// RY landing page behaviour (funnel_events aggregation) — Pages & Masterclass tab
export const getAdminFunnelPageStats = (from, to, paidOnly, extra = {}) =>
  // paidOnly is OMITTED unless true — the endpoints are inert without it,
  // so Pages & Masterclass keeps sending exactly the request it always has.
  api.get('/api/admin/funnel-page-stats', { params: { from, to, ...(paidOnly ? { paidOnly: 1 } : {}), ...extra } });

// Comp/gift-aware RY purchaser counts — Pages & Masterclass tab
// Drop-off through the pre-checkout interstitial, for the expandable panel
// under the "Clicked a CTA" tile. Same from/to/paidOnly contract as its
// neighbours so the panel is filtered identically to the tile it opens beneath.
export const getAdminInterstitialFunnel = (from, to, paidOnly, extra = {}) =>
  // paidOnly is OMITTED unless true — the endpoints are inert without it,
  // and sending paidOnly=0 would read as "explicitly not paid".
  api.get('/api/admin/interstitial-funnel', { params: { from, to, ...(paidOnly ? { paidOnly: 1 } : {}), ...extra } });

// Which CTA was clicked and how far into the video, for BOTH the masterclass
// and sales pages. Fetched separately from the Pages tab's main Promise.all —
// it feeds two collapsed panels, and a failure here must not blank the page.
export const getAdminCtaDetail = (from, to, paidOnly, extra = {}) =>
  api.get('/api/admin/cta-detail', { params: { from, to, ...(paidOnly ? { paidOnly: 1 } : {}), ...extra } });

// The sales page scroll heat map (GET /sales-heatmap) — the SAME from/to/
// paidOnly/gaps contract as /funnel-page-stats, so it describes the same people
// as the sales card it opens from.
export const getAdminSalesHeatmap = (from, to, paidOnly, extra = {}) =>
  api.get('/api/admin/sales-heatmap', { params: { from, to, ...(paidOnly ? { paidOnly: 1 } : {}), ...extra } });

export const getAdminRySalesStats = (from, to, paidOnly, extra = {}) =>
  // paidOnly is OMITTED unless true — the endpoints are inert without it,
  // so Pages & Masterclass keeps sending exactly the request it always has.
  api.get('/api/admin/ry-sales-stats', { params: { from, to, ...(paidOnly ? { paidOnly: 1 } : {}), ...extra } });
export const getAdminHealth = () => api.get('/api/admin/health');
export const getAdminUtmStats = (from, to, extra = {}) =>
  // from/to OMITTED unless supplied — no args keeps sending exactly the
  // all-time request this endpoint has always received.
  api.get('/api/admin/utm-stats', { params: { from, to, ...extra } });
export const sendTestEmails = () => api.post('/api/admin/send-test-emails');
export const sendTestEmail = (key) => api.post(`/api/admin/send-test-email/${key}`);

// Goals & Targets
export const getGoals = (paidOnly, from, to) =>
  api.get('/api/admin/goals', {
    params: { ...(paidOnly ? { paidOnly: 1 } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}) },
  });
export const saveGoal = (data) => api.post('/api/admin/goals', data);

// Export Report
// scope: 'ry' (default, funnel only) | 'all' (whole business). The report is a
// generated FILE, so the scope has to be chosen before generation — it cannot
// be toggled after the fact.
export const exportReport = (from, to, scope) =>
  api.get('/api/admin/export-report', { params: { from, to, scope }, responseType: 'blob' });

// Every email's stored name + trigger description (ry_sequence_emails), read-only.
export const getSequenceEmails = () => api.get('/api/admin/sequence-emails');

// Which sales video was live when (salesVideoPeriods.js). Used ONLY for the
// sales video's watch figures, one block per video: `extra` on the video
// getters carries ?salesVideo= and ?gaps= for that video's window. Every other
// sales page figure follows the tab's date range and sends neither.
export const getSalesVideos = () => api.get('/api/admin/sales-videos');

// Wistia Video Stats
export const getAdminWistiaStats = (from, to, paidOnly, extra = {}) =>
  // paidOnly is OMITTED unless true — the endpoints are inert without it,
  // so Pages & Masterclass keeps sending exactly the request it always has.
  api.get('/api/admin/wistia-stats', { params: { from, to, ...(paidOnly ? { paidOnly: 1 } : {}), ...extra } });

// Admin Settings
export const getAdminSettings = () => api.get('/api/admin/settings');
export const saveAdminSetting = (key, value) => api.post('/api/admin/settings', { key, value });

// ── The windowed discount code (GO50GO) ──────────────────────────────────────
// Status is readable by any admin; arming is owner-only server-side
// (ownerMiddleware, the same gate as POST /settings), and the control that
// calls it is hidden from non-owners so the 403 never surfaces as an error.
export const getDiscountWindow  = () => api.get('/api/admin/discount-window');
export const setDiscountWindow  = (action) => api.post('/api/admin/discount-window', { action });
// The call funnel switch (backend config/callFunnel.js). GET is admin-wide; POST is owner only.
export const getCallFunnel = () => api.get('/api/admin/call-funnel');
export const setCallFunnel = (action, confirm) => api.post('/api/admin/call-funnel', { action, confirm });
export const setCallFunnelSessionLength = (minutes) => api.post('/api/admin/call-funnel/session-length', { minutes });
export const setCallFunnelPeriodTest = (index, test, confirm) => api.post('/api/admin/call-funnel/period-test', { index, test, confirm });
export const getCallFunnelStats = ({ from, to, paidOnly } = {}) =>
  api.get('/api/admin/call-funnel-stats', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}), paidOnly: paidOnly ? '1' : '0' } });

// Funnel Test
export const runFunnelTestApi = () => api.get('/api/admin/funnel-test');
export const cleanupFunnelTestDataApi = () => api.post('/api/admin/funnel-test/cleanup');

// Test-user cleanup (System Health → Test Data Cleanup section)
export const getTestUsersPreview  = () => api.get('/api/admin/test-users-preview');
export const deleteTestUsers      = (userIds) => api.post('/api/admin/test-users-delete', { confirmation: 'DELETE TEST USERS', userIds });

// Missed-BMS follow-up (Overview alert "Send follow-up" button — one click per row)
export const sendMissedBmsFollowup = (userId) => api.post('/api/admin/send-missed-bms-followup', { userId });

// Manual BMS outcome (Stage 1 — write-only; nothing downstream reads it yet).
// outcome: 'show_upsell' | 'show_no_upsell' | 'no_show' | null (null = reschedule/clear).
export const setBmsOutcome = (userId, outcome) => api.post(`/api/admin/clients/${userId}/bms-outcome`, { outcome });

// Login-reminder (Overview "purchased but not logged in" alert — one click per row).
// Owner-only; backend guards against a second password reset.
export const sendLoginReminder = (userId) => api.post('/api/admin/send-login-reminder', { userId });

// Missed-BMS expandable journey (single round-trip — funnel stages + day-by-day progress + journal)
export const getMissedBmsJourney = (userId) => api.get(`/api/admin/missed-bms-journey/${userId}`);

// Owner-only: invite a new view-only admin (creates a viewer + sends a password-reset invite)
export const inviteAdmin = ({ email, first_name, last_name }) =>
  api.post('/api/admin/invite-admin', { email, first_name, last_name });

// Owner-only: admin management — list all admins, remove (flag-flip), and resend invite to pending.
export const listAdmins        = () => api.get('/api/admin/admins');
export const removeAdmin       = (id) => api.delete(`/api/admin/admins/${id}`);
export const resendAdminInvite = (id) => api.post(`/api/admin/admins/${id}/resend`);

// ─── Business costs — the money-out half of the Revenue tab ──────────────────
// Reads are open to any admin; every write is owner-gated server-side.
export const getBusinessCosts = (includeInactive) =>
  api.get('/api/admin/business-costs', {
    params: { ...(includeInactive ? { includeInactive: 1 } : {}) },
  });
export const createBusinessCost     = (data)     => api.post('/api/admin/business-costs', data);
export const updateBusinessCost     = (id, data) => api.patch(`/api/admin/business-costs/${id}`, data);
// Deactivate, NEVER delete — a cost that stopped is still part of the history
// of what the business cost. Removed rows come back via includeInactive.
export const deactivateBusinessCost = (id)       => api.post(`/api/admin/business-costs/${id}/deactivate`);
export const setCostGroups          = (groups)   => api.post('/api/admin/business-costs/groups', { groups });

// The ledger derivation. READ-ONLY — it proposes; apply-derived is what saves.
export const deriveCostsFromLedger  = ()        => api.get('/api/admin/business-costs/derive-from-ledger');
export const applyDerivedCosts      = (costs)   => api.post('/api/admin/business-costs/apply-derived', { costs });
export const dismissLedgerVendor    = (vendor)  => api.post('/api/admin/business-costs/dismiss', { vendor });
export const undismissLedgerVendor  = (vendor)  => api.post('/api/admin/business-costs/undismiss', { vendor });

// Planner state. The POST MERGES into ry_business_plan.payload — the dismissed
// list and the custom group names share that single row.
export const getBusinessPlan        = ()        => api.get('/api/admin/business-plan');
export const saveBusinessPlan       = (patch)   => api.post('/api/admin/business-plan', patch);
